// useEpk — the Assets → EPK data hooks: the editor read (the whole EPK document + the full bio +
// the 5 AUTO stat tiles) and the whole-document PUT. Every read forces the tenant key ['band', bandId, …] via
// useBandQuery; the write invalidates it on success. The editor GET·PUT is epk:write (NOT assets:write — a non-AV
// band_member holds assets:write but is denied EPK; the screen cues with useCapability("epk:write")). Version
// history/restore reuses the shared brand codec hooks in useBrandAssets (scope="epk").
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type EpkRead = components["schemas"]["EpkRead"];
export type EpkQuote = components["schemas"]["EpkQuote"];
export type EpkQuoteInput = components["schemas"]["EpkQuoteInput"];
export type EpkMoment = components["schemas"]["EpkMoment"];
export type EpkMomentInput = components["schemas"]["EpkMomentInput"];
export type EpkStatTiles = components["schemas"]["EpkStatTiles"];
export type UpdateEpkRequest = components["schemas"]["UpdateEpkRequest"];

// The editor read carries LOCAL DRAFT state in the screen, so a BACKGROUND refetch must never clobber unsaved edits:
// staleTime:Infinity + refetchOnWindowFocus/Reconnect:false override the app-client defaults (the
// draft-clobber fix). dataUpdatedAt only moves on the initial load, an explicit Retry, or the save invalidateQueries.
const EDITOR_READ = { staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false } as const;

export function useEpk() {
  const { api } = useApp();
  return useBandQuery<EpkRead>(
    ["epk"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/epk", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useUpdateEpk() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<EpkRead, UpdateEpkRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/epk", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "epk"] }) },
  );
}
